import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  Calendar,
  CheckCircle2,
  Clock,
  Filter,
  PlusCircle,
  Receipt,
  RefreshCw,
  Save,
  Search,
  Tag,
  Users,
  Wallet,
} from "lucide-react";

import {
  GetAdminCoupons,
  GetCouponRegistrations,
  GetCouponTransactions,
} from "./apis/get";
import { PostCreateCoupon } from "./apis/post";
import { PatchUpdateCoupon } from "./apis/patch";

interface CouponSummary {
  id: number;
  code: string;
  name: string | null;
  description: string | null;
  discount_percentage: number;
  max_users: number | null;
  registered_users: number;
  remaining_user_slots: number | null;
  successful_discounted_transactions: number;
  total_discount_amount: number;
  gross_transaction_amount: number;
  final_transaction_amount: number;
  valid_from: string | null;
  valid_until: string | null;
  is_active: boolean;
  status_message: string;
  is_currently_valid: boolean;
  created_by: string | null;
  updated_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface CouponRegistration {
  registration_id: number;
  coupon_id: number;
  coupon_code: string;
  coupon_name: string | null;
  user_id: string;
  username: string | null;
  phone_number: string | null;
  email_address: string | null;
  registered_at: string | null;
}

interface CouponTransaction {
  transaction_db_id: number;
  merchant_transaction_id: string | null;
  payment_ref: string | null;
  transaction_id: string | null;
  transaction_status: string | null;
  user_id: string;
  username: string | null;
  coupon_id: number | null;
  coupon_code: string | null;
  coupon_name: string | null;
  discount_percentage: number | null;
  gross_amount: number | null;
  discount_amount: number | null;
  final_amount: number | null;
  credits: number;
  created_at: string | null;
}

interface CouponFormState {
  code: string;
  name: string;
  description: string;
  discount_percentage: number;
  max_users: string;
  valid_from: string;
  valid_until: string;
  is_active: boolean;
}

const emptyCouponForm: CouponFormState = {
  code: "",
  name: "",
  description: "",
  discount_percentage: 10,
  max_users: "",
  valid_from: "",
  valid_until: "",
  is_active: true,
};

const INPUT_CLASS =
  "w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-slate-200 focus:outline-none focus:border-amber-400 disabled:opacity-50 disabled:cursor-not-allowed";

const normalizeRole = (role: string | null) => {
  const lowered = (role || "").toLowerCase();
  if (["customer_support", "customer_lead", "calling_team_lead"].includes(lowered)) {
    return "support";
  }
  return lowered;
};

const formatCurrency = (amount: number | null | undefined) => {
  return `₹${Number(amount || 0).toLocaleString()}`;
};

const formatDateTime = (value: string | null) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const formatDateInput = (value: string | null) => {
  if (!value) return "";
  return value.slice(0, 16);
};

const statusTone = (coupon: CouponSummary) => {
  if (!coupon.is_active) return "text-rose-400 bg-rose-500/10 border-rose-500/20";
  if (!coupon.is_currently_valid) return "text-amber-400 bg-amber-500/10 border-amber-500/20";
  return "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";
};

const transactionStatusTone = (status: string | null) => {
  switch ((status || "").toUpperCase()) {
    case "SUCCESS":
      return "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";
    case "FAILED":
      return "text-rose-400 bg-rose-500/10 border-rose-500/20";
    case "PENDING":
      return "text-amber-400 bg-amber-500/10 border-amber-500/20";
    default:
      return "text-slate-400 bg-slate-500/10 border-slate-500/20";
  }
};

export default function CouponManagement() {
  const [role, setRole] = useState<string | null>(null);
  const [coupons, setCoupons] = useState<CouponSummary[]>([]);
  const [selectedCouponId, setSelectedCouponId] = useState<number | null>(null);
  const [loadingCoupons, setLoadingCoupons] = useState(true);
  const [registrations, setRegistrations] = useState<CouponRegistration[]>([]);
  const [transactions, setTransactions] = useState<CouponTransaction[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [activeTab, setActiveTab] = useState<"registrations" | "transactions">(
    "registrations",
  );
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "true" | "false">(
    "all",
  );
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState<CouponFormState>(emptyCouponForm);
  const [editForm, setEditForm] = useState<CouponFormState>(emptyCouponForm);
  const [savingCreate, setSavingCreate] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    const data = localStorage.getItem("data");
    if (!data) return;
    try {
      const parsed = JSON.parse(data);
      setRole(parsed.Role || null);
    } catch (error) {
      console.error("Failed to read role for coupon management", error);
    }
  }, []);

  const normalizedRole = normalizeRole(role);
  const canManageCoupons = ["admin", "super_admin"].includes(normalizedRole);

  const selectedCoupon = useMemo(
    () => coupons.find((coupon) => coupon.id === selectedCouponId) || null,
    [coupons, selectedCouponId],
  );

  const couponStats = useMemo(() => {
    const totalCoupons = coupons.length;
    const activeCoupons = coupons.filter((coupon) => coupon.is_active).length;
    const validCoupons = coupons.filter((coupon) => coupon.is_currently_valid).length;
    const totalRegistrations = coupons.reduce(
      (sum, coupon) => sum + (coupon.registered_users || 0),
      0,
    );
    const totalDiscount = coupons.reduce(
      (sum, coupon) => sum + (coupon.total_discount_amount || 0),
      0,
    );

    return {
      totalCoupons,
      activeCoupons,
      validCoupons,
      totalRegistrations,
      totalDiscount,
    };
  }, [coupons]);

  const loadCoupons = useCallback(
    async (
      preserveSelection = true,
      nextSearch = search,
      nextStatusFilter = statusFilter,
    ) => {
      setLoadingCoupons(true);
      try {
        const response = await GetAdminCoupons({
          search: nextSearch || undefined,
          is_active: nextStatusFilter === "all" ? "" : nextStatusFilter,
        });

        if (response?.status) {
          const nextCoupons = response.data || [];
          setCoupons(nextCoupons);

          setSelectedCouponId((current) => {
            if (!nextCoupons.length) return null;
            if (
              preserveSelection &&
              current &&
              nextCoupons.some((coupon: CouponSummary) => coupon.id === current)
            ) {
              return current;
            }
            return nextCoupons[0].id;
          });
        } else {
          setCoupons([]);
          setSelectedCouponId(null);
        }
      } catch (error) {
        console.error("Failed to load coupons", error);
        toast.error("Failed to load coupon dashboard");
      } finally {
        setLoadingCoupons(false);
      }
    },
    [search, statusFilter],
  );

  const loadReports = async (couponId: number | null) => {
    if (!couponId) {
      setRegistrations([]);
      setTransactions([]);
      return;
    }

    setLoadingReports(true);
    try {
      const [registrationsResponse, transactionsResponse] = await Promise.all([
        GetCouponRegistrations({ coupon_id: couponId, limit: 200, offset: 0 }),
        GetCouponTransactions({
          coupon_id: couponId,
          success_only: true,
          limit: 200,
          offset: 0,
        }),
      ]);

      setRegistrations(registrationsResponse?.data || []);
      setTransactions(transactionsResponse?.data || []);
    } catch (error) {
      console.error("Failed to load coupon reports", error);
      toast.error("Failed to load coupon reports");
    } finally {
      setLoadingReports(false);
    }
  };

  useEffect(() => {
    loadCoupons(false);
  }, [loadCoupons]);

  useEffect(() => {
    if (!selectedCoupon) {
      setEditForm(emptyCouponForm);
      return;
    }

    setEditForm({
      code: selectedCoupon.code || "",
      name: selectedCoupon.name || "",
      description: selectedCoupon.description || "",
      discount_percentage: selectedCoupon.discount_percentage || 0,
      max_users:
        selectedCoupon.max_users === null || selectedCoupon.max_users === undefined
          ? ""
          : String(selectedCoupon.max_users),
      valid_from: formatDateInput(selectedCoupon.valid_from),
      valid_until: formatDateInput(selectedCoupon.valid_until),
      is_active: selectedCoupon.is_active,
    });
  }, [selectedCoupon]);

  useEffect(() => {
    loadReports(selectedCouponId);
  }, [selectedCouponId]);

  const applyFilters = async () => {
    const nextSearch = searchInput.trim();
    setSearch(nextSearch);
    await loadCoupons(false, nextSearch, statusFilter);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canManageCoupons) return;

    setSavingCreate(true);
    try {
      const payload: Record<string, any> = {
        code: createForm.code.trim(),
        name: createForm.name.trim() || null,
        description: createForm.description.trim() || null,
        discount_percentage: Number(createForm.discount_percentage || 0),
        is_active: createForm.is_active,
      };

      if (createForm.max_users.trim()) payload.max_users = Number(createForm.max_users);
      if (createForm.valid_from) payload.valid_from = createForm.valid_from;
      if (createForm.valid_until) payload.valid_until = createForm.valid_until;

      const response = await PostCreateCoupon(payload);
      if (response?.status) {
        setCreateForm(emptyCouponForm);
        setShowCreateForm(false);
        await loadCoupons(false);
        if (response?.data?.id) setSelectedCouponId(response.data.id);
      }
    } finally {
      setSavingCreate(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canManageCoupons || !selectedCoupon) return;

    setSavingEdit(true);
    try {
      const payload: Record<string, any> = {
        code: editForm.code.trim(),
        name: editForm.name.trim() || null,
        description: editForm.description.trim() || null,
        discount_percentage: Number(editForm.discount_percentage || 0),
        is_active: editForm.is_active,
      };

      payload.max_users = editForm.max_users.trim()
        ? Number(editForm.max_users)
        : null;
      payload.valid_from = editForm.valid_from || null;
      payload.valid_until = editForm.valid_until || null;

      const response = await PatchUpdateCoupon(selectedCoupon.id, payload);
      if (response?.status) {
        await loadCoupons(true);
      }
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="min-h-screen bg-transparent text-slate-200 p-8 space-y-8">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold bg-gradient-to-r from-amber-300 via-orange-400 to-rose-400 bg-clip-text text-transparent">
            Coupon Command Center
          </h1>
          <p className="text-slate-500 font-medium tracking-tight">
            Manage coupon capacity, registrations, and discount-linked payments from one place.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => loadCoupons(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-2xl text-slate-200 font-bold transition active:scale-95"
          >
            <RefreshCw size={16} className={loadingCoupons ? "animate-spin" : ""} />
            Refresh
          </button>
          {canManageCoupons && (
            <button
              onClick={() => setShowCreateForm((prev) => !prev)}
              className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 border border-amber-300 rounded-2xl font-black transition active:scale-95"
            >
              <PlusCircle size={16} />
              {showCreateForm ? "Hide Create Form" : "Create Coupon"}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-5">
        <StatCard
          label="Total Coupons"
          value={couponStats.totalCoupons.toLocaleString()}
          hint="All configured coupon codes"
          icon={<Tag size={18} />}
          tone="amber"
        />
        <StatCard
          label="Active Coupons"
          value={couponStats.activeCoupons.toLocaleString()}
          hint="Enabled in the system"
          icon={<CheckCircle2 size={18} />}
          tone="emerald"
        />
        <StatCard
          label="Currently Valid"
          value={couponStats.validCoupons.toLocaleString()}
          hint="Live and usable now"
          icon={<Clock size={18} />}
          tone="blue"
        />
        <StatCard
          label="Registrations"
          value={couponStats.totalRegistrations.toLocaleString()}
          hint="Users registered with coupons"
          icon={<Users size={18} />}
          tone="violet"
        />
        <StatCard
          label="Total Discounts"
          value={formatCurrency(couponStats.totalDiscount)}
          hint="Discount amount tracked"
          icon={<Wallet size={18} />}
          tone="rose"
        />
      </div>

      {showCreateForm && canManageCoupons && (
        <form
          onSubmit={handleCreate}
          className="bg-slate-900/60 border border-slate-800 rounded-[2rem] p-6 space-y-5 shadow-2xl"
        >
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-black text-white uppercase tracking-wide">
                Create New Coupon
              </h2>
              <p className="text-slate-500 text-sm">
                Set discount percentage, max user capacity, and validity window.
              </p>
            </div>
            <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase bg-amber-500/10 text-amber-400 border border-amber-500/20">
              Admin Only
            </span>
          </div>

          <CouponFormFields form={createForm} setForm={setCreateForm} disabled={savingCreate} />

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={savingCreate}
              className="flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-950 font-black rounded-2xl transition active:scale-95"
            >
              <Save size={16} />
              {savingCreate ? "Creating..." : "Create Coupon"}
            </button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[380px_minmax(0,1fr)] gap-8">
        <section className="bg-slate-900/40 border border-slate-800 rounded-[2rem] p-5 space-y-5 shadow-2xl h-fit">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl bg-slate-950 border border-slate-800 text-amber-400">
              <Filter size={18} />
            </div>
            <div>
              <h2 className="text-sm font-black text-white uppercase tracking-widest">
                Coupon Directory
              </h2>
              <p className="text-slate-500 text-xs">
                Search and filter all configured coupon codes.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500"
              />
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search code, name, description"
                className="w-full pl-11 pr-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-slate-200 focus:outline-none focus:border-amber-400"
              />
            </div>

            <div className="grid grid-cols-[1fr_auto] gap-3">
              <select
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as "all" | "true" | "false")
                }
                className="px-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-slate-200 focus:outline-none focus:border-amber-400"
              >
                <option value="all">All statuses</option>
                <option value="true">Active only</option>
                <option value="false">Inactive only</option>
              </select>

              <button
                onClick={applyFilters}
                className="px-5 py-3 bg-slate-800 hover:bg-slate-700 rounded-2xl font-bold text-white transition active:scale-95"
              >
                Apply
              </button>
            </div>
          </div>

          <div className="space-y-3 max-h-[900px] overflow-y-auto pr-1">
            {loadingCoupons ? (
              <div className="py-16 text-center text-slate-500 font-bold">
                Loading coupons...
              </div>
            ) : coupons.length === 0 ? (
              <div className="py-16 text-center text-slate-500 font-bold">
                No coupons found for these filters.
              </div>
            ) : (
              coupons.map((coupon) => {
                const isSelected = coupon.id === selectedCouponId;
                return (
                  <button
                    key={coupon.id}
                    onClick={() => setSelectedCouponId(coupon.id)}
                    className={`w-full text-left rounded-[1.5rem] border p-4 transition-all ${
                      isSelected
                        ? "border-amber-400 bg-amber-500/10 shadow-lg shadow-amber-500/10"
                        : "border-slate-800 bg-slate-950/50 hover:border-slate-700"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-white font-black text-lg tracking-tight">
                          {coupon.code}
                        </div>
                        <div className="text-slate-400 text-xs font-semibold">
                          {coupon.name || "Untitled coupon"}
                        </div>
                      </div>
                      <span
                        className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase border ${statusTone(coupon)}`}
                      >
                        {coupon.is_currently_valid ? "Live" : coupon.is_active ? "Limited" : "Inactive"}
                      </span>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                      <div className="rounded-2xl bg-slate-900/70 border border-slate-800 p-3">
                        <div className="text-slate-500 uppercase font-black text-[9px]">
                          Discount
                        </div>
                        <div className="text-white font-black text-base">
                          {coupon.discount_percentage}%
                        </div>
                      </div>
                      <div className="rounded-2xl bg-slate-900/70 border border-slate-800 p-3">
                        <div className="text-slate-500 uppercase font-black text-[9px]">
                          Users
                        </div>
                        <div className="text-white font-black text-base">
                          {coupon.registered_users}
                          {coupon.max_users ? ` / ${coupon.max_users}` : ""}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 text-[11px] text-slate-500 leading-relaxed">
                      {coupon.description || coupon.status_message}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </section>

        <section className="space-y-6">
          {selectedCoupon ? (
            <>
              <div className="bg-slate-900/40 border border-slate-800 rounded-[2rem] p-6 space-y-6 shadow-2xl">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <h2 className="text-2xl font-black text-white tracking-tight">
                        {selectedCoupon.code}
                      </h2>
                      <span
                        className={`px-3 py-1 rounded-full text-[10px] font-black uppercase border ${statusTone(selectedCoupon)}`}
                      >
                        {selectedCoupon.status_message}
                      </span>
                    </div>
                    <p className="text-slate-500 mt-2">
                      {selectedCoupon.description || "No description added for this coupon yet."}
                    </p>
                  </div>

                  {!canManageCoupons && (
                    <div className="px-4 py-2 rounded-2xl bg-blue-500/10 border border-blue-500/20 text-blue-300 text-sm font-semibold">
                      View-only mode for your role
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  <MiniMetric
                    label="Registered Users"
                    value={String(selectedCoupon.registered_users)}
                    helper={
                      selectedCoupon.max_users
                        ? `${selectedCoupon.remaining_user_slots ?? 0} slots left`
                        : "No max-user limit"
                    }
                    icon={<Users size={15} />}
                  />
                  <MiniMetric
                    label="Discounted Txns"
                    value={String(selectedCoupon.successful_discounted_transactions)}
                    helper="Successful coupon-linked payments"
                    icon={<Receipt size={15} />}
                  />
                  <MiniMetric
                    label="Discount Given"
                    value={formatCurrency(selectedCoupon.total_discount_amount)}
                    helper="Tracked from transactions"
                    icon={<Wallet size={15} />}
                  />
                  <MiniMetric
                    label="Validity Window"
                    value={
                      selectedCoupon.valid_until
                        ? new Date(selectedCoupon.valid_until).toLocaleDateString()
                        : "Open"
                    }
                    helper={
                      selectedCoupon.valid_from
                        ? `Starts ${new Date(selectedCoupon.valid_from).toLocaleDateString()}`
                        : "Available immediately"
                    }
                    icon={<Calendar size={15} />}
                  />
                </div>

                <form onSubmit={handleUpdate} className="space-y-5">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-black uppercase tracking-widest text-white">
                      Coupon Details
                    </h3>
                    {canManageCoupons && (
                      <button
                        type="submit"
                        disabled={savingEdit}
                        className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-950 font-black rounded-2xl transition active:scale-95"
                      >
                        <Save size={16} />
                        {savingEdit ? "Saving..." : "Save Changes"}
                      </button>
                    )}
                  </div>

                  <CouponFormFields
                    form={editForm}
                    setForm={setEditForm}
                    disabled={!canManageCoupons || savingEdit}
                  />
                </form>
              </div>

              <div className="bg-slate-900/40 border border-slate-800 rounded-[2rem] p-6 space-y-5 shadow-2xl">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-black uppercase tracking-widest text-white">
                      Coupon Reports
                    </h3>
                    <p className="text-slate-500 text-sm">
                      Track who registered with this coupon and which transactions used it.
                    </p>
                  </div>

                  <div className="flex items-center gap-2 bg-slate-950 p-1 rounded-2xl border border-slate-800">
                    <button
                      onClick={() => setActiveTab("registrations")}
                      className={`px-4 py-2 rounded-xl text-xs font-black uppercase transition ${
                        activeTab === "registrations"
                          ? "bg-amber-500 text-slate-950"
                          : "text-slate-400 hover:text-white"
                      }`}
                    >
                      Registrations
                    </button>
                    <button
                      onClick={() => setActiveTab("transactions")}
                      className={`px-4 py-2 rounded-xl text-xs font-black uppercase transition ${
                        activeTab === "transactions"
                          ? "bg-amber-500 text-slate-950"
                          : "text-slate-400 hover:text-white"
                      }`}
                    >
                      Transactions
                    </button>
                  </div>
                </div>

                {loadingReports ? (
                  <div className="py-16 text-center text-slate-500 font-bold">
                    Loading report data...
                  </div>
                ) : activeTab === "registrations" ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-slate-800">
                          <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-black">
                            User
                          </th>
                          <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-black">
                            Contact
                          </th>
                          <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-black">
                            Registered At
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {registrations.length === 0 ? (
                          <tr>
                            <td
                              colSpan={3}
                              className="px-4 py-12 text-center text-slate-500 font-semibold"
                            >
                              No users have registered with this coupon yet.
                            </td>
                          </tr>
                        ) : (
                          registrations.map((row) => (
                            <tr
                              key={row.registration_id}
                              className="border-b border-slate-800/40 hover:bg-slate-950/40"
                            >
                              <td className="px-4 py-4">
                                <div className="font-black text-white">{row.username || "Unknown user"}</div>
                                <div className="text-xs text-slate-500">{row.user_id}</div>
                              </td>
                              <td className="px-4 py-4">
                                <div className="text-sm text-slate-300">
                                  {row.phone_number || "No phone"}
                                </div>
                                <div className="text-xs text-slate-500">
                                  {row.email_address || "No email"}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-slate-300">
                                {formatDateTime(row.registered_at)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-slate-800">
                          <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-black">
                            User
                          </th>
                          <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-black">
                            Transaction
                          </th>
                          <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-black">
                            Discount
                          </th>
                          <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-black">
                            Amounts
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.length === 0 ? (
                          <tr>
                            <td
                              colSpan={4}
                              className="px-4 py-12 text-center text-slate-500 font-semibold"
                            >
                              No coupon-linked successful transactions found.
                            </td>
                          </tr>
                        ) : (
                          transactions.map((row) => (
                            <tr
                              key={row.transaction_db_id}
                              className="border-b border-slate-800/40 hover:bg-slate-950/40"
                            >
                              <td className="px-4 py-4">
                                <div className="font-black text-white">{row.username || "Unknown user"}</div>
                                <div className="text-xs text-slate-500">{row.user_id}</div>
                              </td>
                              <td className="px-4 py-4">
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase border ${transactionStatusTone(row.transaction_status)}`}
                                  >
                                    {row.transaction_status || "UNKNOWN"}
                                  </span>
                                  <span className="text-xs text-slate-500">
                                    {row.merchant_transaction_id || row.payment_ref || "No ref"}
                                  </span>
                                </div>
                                <div className="text-xs text-slate-500 mt-1">
                                  {formatDateTime(row.created_at)}
                                </div>
                              </td>
                              <td className="px-4 py-4">
                                <div className="font-black text-emerald-400">
                                  {row.discount_percentage || 0}% off
                                </div>
                                <div className="text-xs text-slate-500">
                                  Saved {formatCurrency(row.discount_amount)}
                                </div>
                              </td>
                              <td className="px-4 py-4">
                                <div className="text-sm text-slate-300">
                                  Gross: {formatCurrency(row.gross_amount)}
                                </div>
                                <div className="text-sm text-white font-black">
                                  Paid: {formatCurrency(row.final_amount)}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="bg-slate-900/40 border border-slate-800 rounded-[2rem] p-16 text-center text-slate-500 font-semibold">
              Select a coupon from the left to view and manage it.
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function CouponFormFields({
  form,
  setForm,
  disabled,
}: {
  form: CouponFormState;
  setForm: React.Dispatch<React.SetStateAction<CouponFormState>>;
  disabled?: boolean;
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <FieldWrap label="Coupon Code">
        <input
          value={form.code}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))
          }
          disabled={disabled}
          className={INPUT_CLASS}
          placeholder="SAVE10"
        />
      </FieldWrap>

      <FieldWrap label="Display Name">
        <input
          value={form.name}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          disabled={disabled}
          className={INPUT_CLASS}
          placeholder="Summer Offer"
        />
      </FieldWrap>

      <FieldWrap label="Discount Percentage">
        <input
          type="number"
          min={0}
          max={100}
          value={form.discount_percentage}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              discount_percentage: Number(e.target.value || 0),
            }))
          }
          disabled={disabled}
          className={INPUT_CLASS}
        />
      </FieldWrap>

      <FieldWrap label="Max Users">
        <input
          type="number"
          min={1}
          value={form.max_users}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, max_users: e.target.value }))
          }
          disabled={disabled}
          className={INPUT_CLASS}
          placeholder="Leave blank for unlimited"
        />
      </FieldWrap>

      <FieldWrap label="Valid From">
        <input
          type="datetime-local"
          value={form.valid_from}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, valid_from: e.target.value }))
          }
          disabled={disabled}
          className={INPUT_CLASS}
        />
      </FieldWrap>

      <FieldWrap label="Valid Until">
        <input
          type="datetime-local"
          value={form.valid_until}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, valid_until: e.target.value }))
          }
          disabled={disabled}
          className={INPUT_CLASS}
        />
      </FieldWrap>

      <div className="md:col-span-2">
        <FieldWrap label="Description">
          <textarea
            value={form.description}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, description: e.target.value }))
            }
            disabled={disabled}
            className={`${INPUT_CLASS} min-h-[110px] resize-y`}
            placeholder="Explain where and why this coupon should be used."
          />
        </FieldWrap>
      </div>

      <div className="md:col-span-2 flex items-center justify-between rounded-2xl border border-slate-800 bg-slate-950 px-4 py-3">
        <div>
          <div className="text-white font-bold text-sm">Coupon Active</div>
          <div className="text-slate-500 text-xs">
            Inactive coupons cannot be used even if the dates are valid.
          </div>
        </div>
        <button
          type="button"
          disabled={disabled}
          onClick={() =>
            setForm((prev) => ({ ...prev, is_active: !prev.is_active }))
          }
          className={`relative inline-flex h-7 w-14 items-center rounded-full transition ${
            form.is_active ? "bg-emerald-500" : "bg-slate-700"
          } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
              form.is_active ? "translate-x-8" : "translate-x-1"
            }`}
          />
        </button>
      </div>
    </div>
  );
}

function FieldWrap({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="space-y-2 block">
      <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-black">
        {label}
      </span>
      {children}
    </label>
  );
}

function StatCard({
  label,
  value,
  hint,
  icon,
  tone,
}: {
  label: string;
  value: string;
  hint: string;
  icon: React.ReactNode;
  tone: "amber" | "emerald" | "blue" | "violet" | "rose";
}) {
  const toneMap = {
    amber: "text-amber-400 bg-amber-500/10 border-amber-500/20",
    emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
    blue: "text-blue-400 bg-blue-500/10 border-blue-500/20",
    violet: "text-violet-400 bg-violet-500/10 border-violet-500/20",
    rose: "text-rose-400 bg-rose-500/10 border-rose-500/20",
  };

  return (
    <div className="bg-slate-900/40 border border-slate-800 rounded-[2rem] p-5 shadow-xl">
      <div className="flex items-center justify-between mb-5">
        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
          {label}
        </span>
        <div className={`p-3 rounded-2xl border ${toneMap[tone]}`}>{icon}</div>
      </div>
      <div className="text-3xl font-black text-white tracking-tight">{value}</div>
      <div className="text-xs text-slate-500 mt-2">{hint}</div>
    </div>
  );
}

function MiniMetric({
  label,
  value,
  helper,
  icon,
}: {
  label: string;
  value: string;
  helper: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-[1.5rem] bg-slate-950/70 border border-slate-800 p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-black">
          {label}
        </span>
        <div className="text-amber-400">{icon}</div>
      </div>
      <div className="text-2xl font-black text-white">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{helper}</div>
    </div>
  );
}
